import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../config/api.config';
import { generateSalt, hashPassword } from '../utils/passwordUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';


// Islamic values and messages
const ISLAMIC_MESSAGES = {
  title: "Renew Your Trust",
  subtitle: "Set a new password to continue your journey with HalalCred",
  success: "Your password has been renewed successfully.",
  invalidLink: "This link has expired or is no longer valid. Please request a new one.",
  backToSignIn: "Return to Sign In",
  resetPassword: "Renew Password",
  resettingPassword: "Renewing Password...",
  newPassword: "New Password",
  confirmPassword: "Confirm New Password",
  passwordRequirements: "Password must be at least 8 characters long",
  passwordMismatch: "Passwords do not match. Please ensure both entries are identical.",
  resetFailed: "Unable to renew password. Please try again with patience.",
  invalidToken: "Invalid or Expired Link",
  invalidTokenMessage: "This password renewal link is invalid or has expired. Please request a new one through the sign-in page."
};

// Password validation utility
const validatePassword = (password, confirmPassword) => {
  if (password.length < 8) {
    return { isValid: false, message: ISLAMIC_MESSAGES.passwordRequirements };
  }
  if (password !== confirmPassword) {
    return { isValid: false, message: ISLAMIC_MESSAGES.passwordMismatch };
  }
  return { isValid: true, message: '' };
};

// Error display component
const ErrorMessage = ({ message }) => {
  if (!message) return null;
  
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
      {message}
    </div>
  );
};

// Success message component
const SuccessMessage = ({ message }) => {
  if (!message) return null;
  
  return (
    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm mb-4">
      {message}
    </div>
  );
};

// Invalid token component
const InvalidTokenView = ({ onBackToSignIn }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#f8fafc] to-[#e2e8f0] px-4">
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <CardTitle className="text-xl font-semibold text-gray-900">
          {ISLAMIC_MESSAGES.invalidToken}
        </CardTitle>
        <CardDescription className="text-gray-600">
          {ISLAMIC_MESSAGES.invalidTokenMessage}
        </CardDescription>
      </CardHeader>
      <CardFooter className="flex justify-center">
        <Button 
          onClick={onBackToSignIn}
          variant="outline"
          className="w-full border-emerald-500 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
        >
          {ISLAMIC_MESSAGES.backToSignIn}
        </Button>
      </CardFooter>
    </Card>
  </div>
);

// Main reset password form component
const ResetPasswordForm = ({ token, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('ResetPassword: Form submission started');
    
    const validation = validatePassword(formData.password, formData.confirmPassword);
    if (!validation.isValid) {
      setError(validation.message);
      console.log('ResetPassword: Validation failed:', validation.message);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('ResetPassword: Generating salt and hashing password');
      const salt = generateSalt();
      const hashedPassword = await hashPassword(formData.password, salt);

      console.log('ResetPassword: Sending reset request to API');
      const response = await fetch(`${getApiUrl()}/auth/auth/reset-password/set`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password: hashedPassword,
          salt,
          clientHash: true
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || ISLAMIC_MESSAGES.resetFailed);
      }

      console.log('ResetPassword: Password reset successful');
      
      // Clear sensitive data
      setFormData({ password: '', confirmPassword: '' });
      
      // Show success message and redirect
      alert(ISLAMIC_MESSAGES.success);
      onSuccess();
    } catch (error) {
      console.error('ResetPassword: Error during password reset:', error);
      setError(error.message || ISLAMIC_MESSAGES.resetFailed);
      onError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <ErrorMessage message={error} />
      
      <div className="space-y-2">
        <Input
          type="password"
          name="password"
          placeholder={ISLAMIC_MESSAGES.newPassword}
          value={formData.password}
          onChange={handleChange}
          required
          disabled={isLoading}
          autoComplete="new-password"
          minLength="8"
          className="h-11"
        />
      </div>
      
      <div className="space-y-2">
        <Input
          type="password"
          name="confirmPassword"
          placeholder={ISLAMIC_MESSAGES.confirmPassword}
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          disabled={isLoading}
          autoComplete="new-password"
          minLength="8"
          className="h-11"
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 font-semibold py-3 rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg"
        disabled={isLoading}
      >
        {isLoading ? ISLAMIC_MESSAGES.resettingPassword : ISLAMIC_MESSAGES.resetPassword}
      </Button>
    </form>
  );
};

// Main component
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const handleSuccess = () => {
    console.log('ResetPassword: Navigating to partner sign-in after successful reset');
    navigate('/partner-signin');
  };

  const handleError = (error) => {
    console.error('ResetPassword: Error occurred:', error);
  };

  const handleBackToSignIn = () => {
    console.log('ResetPassword: Navigating back to sign-in');
    navigate('/partner-signin');
  };

  // SEO-friendly page title
  React.useEffect(() => {
    document.title = 'Reset Password - HalalCred | Islamic Financing Solutions';
  }, []);

  if (!token) {
    return <InvalidTokenView onBackToSignIn={handleBackToSignIn} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#f8fafc] to-[#e2e8f0] px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold text-gray-900">
            {ISLAMIC_MESSAGES.title}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {ISLAMIC_MESSAGES.subtitle}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <ResetPasswordForm 
            token={token}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        </CardContent>
        
        <CardFooter className="flex justify-center">
          <Button 
            onClick={handleBackToSignIn}
            variant="ghost"
            className="text-emerald-600 hover:text-emerald-700"
          >
            {ISLAMIC_MESSAGES.backToSignIn}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ResetPassword; 